import { IScreenRecordingBridgeClient, RecordingStartResult, RecordingErrorType } from './IScreenRecordingBridgeClient';
import {
  ScreenRecordingMessageType,
  ScreenRecordingInitializeMessage,
  ScreenRecordingSaveChunkMessage,
  ScreenRecordingCleanupMessage,
  RecordingMetadata
} from '../../../../serviceworker/src/bridges/screenRecording/ScreenRecordingMessages';

const swc = ((navigator as Navigator).serviceWorker as ServiceWorkerContainer);

/**
 * ServiceWorkerBridgeClient for screen recording
 * 
 * This class handles screen recording on the frontend and communicates with the service worker
 * for storage and management of recording chunks. It implements a low-quality, low-bandwidth
 * screen recording solution that captures the user's screen at 1 FPS with low bitrate.
 * 
 * The recording is split into 3-minute chunks to manage memory usage and provide
 * better reliability. If recording fails, the client will automatically attempt to
 * restart it up to 3 times.
 * 
 * @implements {IScreenRecordingBridgeClient}
 */
export default class ScreenRecordingBridgeClient implements IScreenRecordingBridgeClient {
  // Worker messaging
  private pendingMessages: any[] = [];

  // Recording state
  private mediaRecorder: MediaRecorder | null = null;
  private recordingStream: MediaStream | null = null;
  private options: RecordingMetadata | null = null;
  private chunkNumber = 0;
  private recordingInterval: number | null = null;
  private isRecordingActive = false;
  private retryAttempts = 0;

  // Constants
  private readonly CHUNK_DURATION = 3 * 60 * 1000; // 3 minutes — balances chunk count vs corruption risk
  private readonly FRAME_RATE = 1; // 1 fps
  private readonly VIDEO_BITRATE = 375000; // 375 kbps is sufficient for low-fps audit recordings
  private readonly MAX_RETRY_ATTEMPTS = 3;

  /**
   * Initializes the ScreenRecordingBridgeClient and sets up message handlers.
   */
  constructor() {
    this.setupMessageHandler();
  }

  /**
   * Sets up the message handler for the service worker.
   * 
   * @private
   */
  private setupMessageHandler(): void {
    swc.addEventListener('message', this.handleServiceWorkerMessage.bind(this));
  }

  /**
   * Starts recording the screen with the given assessment and user identifiers.
   * 
   * This method initializes the recording metadata, sends initialization messages
   * to the service worker, and begins capturing the screen. The recording uses
   * low quality settings (1 FPS, 1 Mbps) to minimize resource usage while still
   * capturing user activity.
   * 
   * If recording is already active, the method will return an error result.
   * 
   * @param assessmentUUID - Unique identifier for the assessment being recorded
   * @param userUUID - Unique identifier for the user being recorded
   * @returns Promise that resolves with result indicating success/failure and error details
   */
  public async startRecording(assessmentUUID: string, userUUID: string): Promise<RecordingStartResult> {
    if (this.isRecordingActive) {
      console.warn('Recording is already active. Stop current recording first.');
      return {
        success: false,
        error: {
          type: RecordingErrorType.ALREADY_RECORDING,
          message: 'Recording is already active. Stop current recording first.'
        }
      };
    }    
    
    this.options = {
      assessmentUUID,
      userUUID,
      timestamp: Date.now()
    };

    console.log('Starting screen recording...', this.options);

    try {
      // Initialize recording in service worker
      await this.sendMessageToServiceWorker(new ScreenRecordingInitializeMessage(this.options));
      
      // Start the actual recording
      const result = await this.initializeRecording();
      
      if (result.success) {
        console.log('Screen recording started successfully');
      }
      
      return result;
    } catch (error) {
      console.error('Failed to start screen recording:', error);
      return {
        success: false,
        error: {
          type: RecordingErrorType.UNKNOWN,
          message: error instanceof Error ? error.message : 'Unknown error occurred'
        }
      };
    }
  }

  /**
   * Stops the current recording and cleans up resources.
   * 
   * This method:
   * - Stops the media recorder
   * - Stops all media tracks
   * - Clears the recording interval
   * - Sends cleanup message to the service worker
   * - Resets all recording state
   * 
   * If no recording is active, a warning will be logged but no error will be thrown.
   * 
   * @returns Promise that resolves when recording has been successfully stopped
   */
  public async stopRecording(): Promise<void> {
    if (!this.isRecordingActive) {
      console.warn('No active recording to stop.');
      return;
    }

    console.log('Stopping screen recording...');
    this.isRecordingActive = false;
    
    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
      this.recordingInterval = null;
    }
    
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
    }
    
    if (this.recordingStream) {
      this.recordingStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
    }
    
    // Clean up recording in service worker
    if (this.options) {
      await this.sendMessageToServiceWorker(new ScreenRecordingCleanupMessage(this.options));
    }
    
    this.mediaRecorder = null;
    this.recordingStream = null;
    this.chunkNumber = 0;
    this.retryAttempts = 0;
    
    console.log('Screen recording stopped');
  }

  /**
   * Checks if a recording is currently active.
   * 
   * @returns True if recording is active, false otherwise
   */
  public isRecording(): boolean {
    return this.isRecordingActive;
  }

  /**
   * Handles messages received from the service worker.
   * 
   * Processes success and error messages, logging appropriate information.
   * 
   * @private
   * @param event - Message event from the service worker
   */
  private handleServiceWorkerMessage(event: MessageEvent): void {
    const message = event.data;
    
    // Check if this message is for us
    if (!message || !message.type || !Object.values(ScreenRecordingMessageType).includes(message.type)) {
      return; // Not our message
    }
    
    switch (message.type) {
      case ScreenRecordingMessageType.SUCCESS:
        console.log(`Screen recording operation successful: ${message.operationType}`, message.details);
        break;
      case ScreenRecordingMessageType.ERROR:
        console.error(`Screen recording error (${message.errorCode}): ${message.errorMessage}`);
        break;
      default:
        console.log('Received message from service worker:', message);
    }
  }

  /**
   * Sends a message to the service worker.
   * 
   * @private
   * @param message - Message to send to the service worker
   * @returns Promise that resolves when the message is sent
   */
  private async sendMessageToServiceWorker(message: any): Promise<void> {
    if (!swc.controller) {
      console.error('Service Worker not available or not controlling the page.');
      return;
    }
    
    // Add enhanced debugging for message transmission
    if (message.type === ScreenRecordingMessageType.SAVE_CHUNK) {
      console.debug('Sending recording chunk to service worker:', {
        type: message.type,
        chunkNumber: message.chunkNumber,
        blobSize: message.chunkBlob?.size,
        metadata: {
          assessmentUUID: message.metadata?.assessmentUUID,
          userUUID: message.metadata?.userUUID,
          timestamp: message.metadata?.timestamp
        }
      });
    }
    
    // Ensure message is properly serialized before sending to prevent property loss
    swc.controller.postMessage(message);
  }

  /**
   * Initializes the screen recording with low quality settings.
   * 
   * This method:
   * - Requests screen sharing permission from the user
   * - Creates a MediaRecorder with low quality settings (1 FPS, 1 Mbps)
   * - Sets up recording handlers
   * - Starts the first recording chunk
   * - Sets up an interval to rotate chunks every 30 seconds
   * 
   * @private
   * @returns Promise that resolves with result indicating success/failure and error details
   */
  private async initializeRecording(): Promise<RecordingStartResult> {
    try {
      // Check if getDisplayMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        return {
          success: false,
          error: {
            type: RecordingErrorType.NOT_SUPPORTED,
            message: 'Screen recording is not supported in this browser'
          }
        };
      }

      this.recordingStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: { ideal: this.FRAME_RATE },
          displaySurface: 'monitor',          
          width: { ideal: 1280 }, // Higher resolution to allow for higher bitrate
          height: { ideal: 720 },
        },
        preferCurrentTab: false,
        monitorTypeSurfaces: 'include'
      } as DisplayMediaStreamOptions);

      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        this.recordingStream.getTracks().forEach(track => track.stop());
        return {
          success: false,
          error: {
            type: RecordingErrorType.NOT_SUPPORTED,
            message: 'MediaRecorder is not supported in this browser'
          }
        };
      }

      // Force a higher bitrate by using a higher value
      // Browser often cap bitrate based on resolution and framerate
      // so we set a much higher target to get closer to our desired bitrate
      const options: MediaRecorderOptions = {
        mimeType: 'video/webm;codecs=vp8',
        videoBitsPerSecond: this.VIDEO_BITRATE // Request higher bitrate to compensate for browser adjustments
      };

      this.mediaRecorder = new MediaRecorder(this.recordingStream, options);
      this.setupRecordingHandlers();
      this.startNewChunk();
      this.isRecordingActive = true;

      // Set up chunk rotation
      this.recordingInterval = window.setInterval(() => {
        if (this.isRecordingActive) {
          this.rotateChunk();
        }
      }, this.CHUNK_DURATION);

      // Monitor recording state
      this.recordingStream.addEventListener('inactive', () => {
        this.handleRecordingFailure();
      });

      return { success: true };
    } catch (error) {
      console.error('Failed to initialize recording:', error);
      
      // Determine error type based on the specific error
      let errorType: RecordingErrorType = RecordingErrorType.UNKNOWN;
      let errorMessage = 'Unknown error occurred';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check for permission denied errors
        if (error.name === 'NotAllowedError' || 
            error.message.toLowerCase().includes('permission') ||
            error.message.toLowerCase().includes('denied') ||
            error.message.toLowerCase().includes('user denied')) {
          errorType = RecordingErrorType.PERMISSION_DENIED;
          errorMessage = 'User denied screen capture permission';
        } else if (error.name === 'NotSupportedError' ||
                   error.message.toLowerCase().includes('not supported')) {
          errorType = RecordingErrorType.NOT_SUPPORTED;
          errorMessage = 'Screen recording is not supported';
        }
      }
      
      // Clean up any partial initialization
      if (this.recordingStream) {
        this.recordingStream.getTracks().forEach(track => track.stop());
        this.recordingStream = null;
      }
      
      return {
        success: false,
        error: {
          type: errorType,
          message: errorMessage
        }
      };
    }
  }

  /**
   * Sets up handlers for recording events.
   * 
   * Configures handlers for data availability and errors.
   * When data becomes available, it is sent to the service worker.
   * 
   * @private
   */
  private setupRecordingHandlers(): void {
    if (!this.mediaRecorder) return;

    this.mediaRecorder.ondataavailable = async (event: BlobEvent) => {
      if (event.data.size > 0 && this.options) {
        console.log(`Received recording chunk ${this.chunkNumber} (${event.data.size} bytes)`);
        
        // Ensure metadata is properly formatted and not null/undefined
        const metadata = {
          assessmentUUID: this.options.assessmentUUID || 'unknown',
          userUUID: this.options.userUUID || 'unknown',
          timestamp: this.options.timestamp || Date.now()
        };
        
        await this.sendMessageToServiceWorker(new ScreenRecordingSaveChunkMessage(
          event.data,
          this.chunkNumber,
          metadata
        ));
      }
    };

    this.mediaRecorder.onerror = async () => {
      await this.handleRecordingFailure();
    };
  }

  /**
   * Starts a new recording chunk.
   * 
   * This method starts the MediaRecorder if it is in an inactive state.
   * 
   * @private
   */
  private startNewChunk(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'inactive') {
      this.mediaRecorder.start();
    }
  }

  /**
   * Rotates to a new recording chunk.
   * 
   * This method stops the current chunk, increments the chunk counter,
   * and starts a new chunk. This helps manage memory usage and provides
   * better resilience against failures.
   * 
   * @private
   */
  private rotateChunk(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
      this.chunkNumber++;
      this.startNewChunk();
      console.log(`Started new recording chunk ${this.chunkNumber}`);
    }
  }

  /**
   * Handles recording failures with a retry mechanism.
   * 
   * If the recording fails (due to user cancellation, permission issues, etc.),
   * this method will attempt to restart the recording up to MAX_RETRY_ATTEMPTS times.
   * If all retry attempts fail, the recording will be stopped completely.
   * 
   * @private
   * @returns Promise that resolves when handling is complete
   */
  private async handleRecordingFailure(): Promise<void> {
    if (!this.isRecordingActive) return;

    if (this.retryAttempts < this.MAX_RETRY_ATTEMPTS) {
      this.retryAttempts++;
      console.log(`Attempting to restart recording (attempt ${this.retryAttempts})`);
      
      // Clean up but don't fully stop (preserve metadata)
      this.isRecordingActive = false;
      
      if (this.recordingInterval) {
        clearInterval(this.recordingInterval);
        this.recordingInterval = null;
      }
      
      if (this.mediaRecorder) {
        this.mediaRecorder.stop();
      }
      
      if (this.recordingStream) {
        this.recordingStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      }
      
      this.mediaRecorder = null;
      this.recordingStream = null;
      
      // Wait a bit before retrying
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Restart recording
      await this.initializeRecording();
    } else {
      console.error('Max retry attempts reached. Stopping recording.');
      await this.stopRecording();
    }
  }
} 
